import React, { useState } from "react";

import { EventDto, IEvent, IEventSummary } from "@eagerdog/interfaces";

import { apiService } from "src/services/api.service";
import { toast } from "src/components/Toast/ToastManager";
import { Modal } from "src/components/Modal/Modal";
import Confirm from "src/components/Confirm/Confirm";

type FileField = "premium_file_url" | "flyer_image_url";

interface IProps {
  event: IEventSummary;
  field: FileField;
  /** Noun used in button text and toasts: "Premium", "Flyer". */
  label: string;
  /** Accept attribute, e.g. "application/pdf" or "image/jpeg,image/png,image/webp". */
  accept: string;
  /** Mime prefixes/types allowed; checked client-side before upload. */
  allowedTypes?: string[];
  maxBytes: number;
  onUpdated: (event: IEventSummary) => void;
}

/**
 * Upload / delete one file attached to an event (the Premium PDF, the Flyer
 * image). Renders the `label.uploadDocument` markup the event page already
 * styles, and saves by PUTting the whole event like the original Premium
 * handler did.
 */
const EventFileUpload: React.FC<IProps> = ({
  event,
  field,
  label,
  accept,
  allowedTypes,
  maxBytes,
  onUpdated,
}) => {
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const current = event[field];

  const save = (value: string, verb: "Upload" | "Delete") => {
    setBusy(true);
    const { __v: _v, ...fields } = event as EventDto & { __v?: number };
    return apiService.updateEvent(event._id, { ...fields, [field]: value } as EventDto)
      .then((response: IEvent) => {
        onUpdated({ ...event, [field]: response[field] });
        toast.show({
          title: `${label} ${verb}`,
          content: verb === "Upload"
            ? `The ${label.toLowerCase()} has been successfully uploaded`
            : `The ${label.toLowerCase()} has been successfully deleted`,
          duration: 10000,
          type: "success",
        });
      })
      .catch(() => {
        toast.show({
          title: `${label} ${verb}`,
          content: verb === "Upload"
            ? `Could not upload the ${label.toLowerCase()}, please try again or select another file`
            : `Could not delete the ${label.toLowerCase()}, please try to refresh the page`,
          duration: 10000,
          type: "fail",
        });
      })
      .finally(() => {
        setBusy(false);
        setConfirmDelete(false);
      });
  };

  const onSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    if (allowedTypes && !allowedTypes.some((t) => file.type === t || (t.endsWith("/") && file.type.startsWith(t)))) {
      toast.show({ title: `${label} Upload`, content: `Please choose a ${accept.replace(/image\//g, "").replace(/,/g, ", ").toUpperCase()} file`, duration: 10000, type: "fail" });
      return;
    }
    if (file.size > maxBytes) {
      toast.show({ title: `${label} Upload`, content: `File is too big, please select a smaller file (max ${Math.round(maxBytes / 1048576)} MB)`, duration: 10000, type: "fail" });
      return;
    }

    setBusy(true);
    apiService.uploadFile(file)
      .then((response: any) => save(typeof response === "string" ? response : response.value, "Upload"))
      .catch(() => {
        setBusy(false);
        toast.show({
          title: `${label} Upload`,
          content: `Could not upload the ${label.toLowerCase()}, please try again or select another file`,
          duration: 10000,
          type: "fail",
        });
      });
  };

  return (
    <>
      <label className="uploadDocument">
        <input type="file" accept={accept} onChange={onSelect} />
        {!busy && (
          <>
            {!current && <span className="uploadBtn">Upload {label}</span>}
            {current && (
              <span
                className="uploadBtn"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setConfirmDelete(true);
                }}
              >
                Delete {label}
              </span>
            )}
          </>
        )}
        {busy && <div id="loading"></div>}
      </label>
      <Modal
        className="Confirmation"
        isShown={confirmDelete}
        hide={() => setConfirmDelete(false)}
        modalContent={
          <Confirm
            message={<p>Are you sure you want to delete the {label.toLowerCase()}?</p>}
            onConfirm={() => save("", "Delete")}
            onCancel={() => setConfirmDelete(false)}
            submitting={busy}
          />
        }
      />
    </>
  );
};

export default EventFileUpload;
